import React from "react";
import { Link } from "gatsby";
import facebookIcon from "@images/icons/facebook-icon.svg";
import instagramIcon from "@images/icons/instagram-icon.svg";
import buttonIcon from "@images/icons/button-icon.svg";
import ErrorComponent from "@components/Error";
import learnMoreIcon from "@images/icons/learn-more.svg";
import watchVideoIcon from "@images/icons/watch.svg";
import downloadIcon from "@images/icons/download.svg";


/**
 * CTA (Call to Action) component for rendering different types of interactive elements
 * 
 * @param {Object} props - Component props
 * @param {string} props.displayType - Type of CTA to display (Button, Link, Link Type Button, etc.)
 * @param {string} props.label - Text to display on the CTA
 * @param {string} props.href - URL for links (defaults to "#")
 * @param {string} props.target - Target attribute for links (defaults to "_self")
 * @param {boolean} props.disabled - Whether the CTA is disabled (for buttons)
 * @param {string} props.style - Additional CSS classes to apply
 * @param {Function} props.onError - Optional callback for error handling
 * @param {Function} props.onClick - Optional callback for button clicks
 * @returns {React.ReactElement} Rendered CTA component
 */
export default function Cta({
    displayType,
    label,
    href = "#",
    target = "_self",
    disabled = false,
    style = "",
    theme,
    onError,
    onClick,
    children,
    className
}) {



    // Error handling
    const handleError = (message) => {
        console.error(message);
        if (onError) onError(message);
        return <ErrorComponent message={message} />;
    };
    const buttonStyle = {
        disabled: `opacity-50`,
        enabled: `inline-flex items-center justify-center rounded-full px-8 text-[14px] lg:text-[16px]  lg:py-6 py-4 font-bold font-gt_walsheim text-[#65F57] border-gradient-button disabled:cursor-not-allowed`,
        light: `text-dark_green hover:bg-soft_green/30 hover:border-gradient-button-hover focus:bg-soft_green/30 focus:shadow-[0px_0px_50px_0px_rgba(0,0,0,0.05)_inset,0px_20px_50px_-20px_rgba(0,0,0,0.20)]`,
        dark: `text-white hover:bg-[#0B302C33] hover:border-gradient-button-hover focus:bg-[#0B302C33] focus:shadow-[0px_0px_50px_0px_rgba(0,0,0,0.05)_inset,0px_20px_50px_-20px_rgba(0,0,0,0.20)]`,
    }
    const themeStyle = `${theme === 'Light' ? buttonStyle.light : buttonStyle.dark}`;
    const disabledStyle = `${disabled ? buttonStyle.disabled : ""}`;
    const styling = `${buttonStyle.enabled} ${themeStyle} ${disabledStyle} ${style}`;

    const renderCta = (type, ctaLabel = label) => {

        try {
            switch (type) {
                case "Button":
                    return (
                        <button
                            displayType={displayType}
                            className={`${styling} ${themeStyle} ${className}`}
                            disabled={disabled}
                            onClick={onClick}
                            label={ctaLabel || ""}
                        >
                            {ctaLabel || ""}
                            <img
                                src={buttonIcon}
                                alt="Button Icon"
                                className="ml-2 h-[12px] w-[12px]"
                            />
                        </button>
                    );
                case "Link":
                    // Check if link is external or PDF
                    const isExternal = href && (href.startsWith('http') || href.startsWith('//') || href.startsWith('www'));
                    if (isExternal) {
                        return (
                            <a
                                data-resource-type="isexternal"
                                href={href}
                                target={target === "Same" ? "_self" : "_blank"}
                                className={`text-[#7AC593] hover:no-underline ${style} ${className}`}
                                onClick={(e) => {
                                    if (onClick) {
                                        e.preventDefault();
                                        onClick(e);
                                    }
                                }}
                            >
                                {ctaLabel ? ctaLabel : null}
                                {children ? children : null}
                            </a>
                        );
                    } else {
                        // For internal links, use Gatsby Link component
                        return (
                            <Link
                                data-resource-type="notexternal"
                                to={href}
                                target={target === "Same" ? "_self" : "_blank"}
                                className={`text-[#7AC593] hover:no-underline ${style} ${className}`}
                                rel="noopener noreferrer"
                                onClick={(e) => {
                                    if (onClick) {
                                        e.preventDefault();
                                        onClick(e);
                                    }
                                }}
                            >
                                {ctaLabel ? ctaLabel : null}
                                {children ? children : null}
                            </Link>
                        );
                    }
                case "Download":

                    return (
                        <Link
                            to={href}
                            target={target === "Same" ? "_self" : "_blank"}
                            className={`text-[#7AC593] hover:no-underline ${style} ${className}`}
                            onClick={(e) => {
                                if (onClick) {
                                    e.preventDefault();
                                    onClick(e);
                                }
                            }}
                        >
                            {ctaLabel ? ctaLabel : null}
                            {children ? children : null}
                        </Link>
                    );
                case "Link Type Button":
                    // Check if browser environment is available
                    const isInternalLink = href && (href.startsWith('http') || href.startsWith('//') || href.startsWith('www'));
                    return (
                        <>
                            {isInternalLink ? (
                                <Link
                                    data-resource-type="notexternal"
                                    to={href}
                                    target={target === "Same" ? "_self" : "_blank"}
                                    className={`${styling} ${theme} ${className}`}
                                    onClick={(e) => {
                                        if (onClick) {
                                            e.preventDefault();
                                            onClick(e);
                                        }
                                    }}
                                >
                                    {ctaLabel || ""}
                                    <img
                                        src={buttonIcon}
                                        alt="Button Icon"
                                        className="ml-2 h-[12px] w-[12px]"
                                    />
                                </Link>
                            ) : (
                                <a
                                    href={href}
                                    data-resource-type="external"
                                    target={target === "Same" ? "_self" : "_blank"}
                                    className={`${styling} ${theme} ${className}`}
                                    onClick={(e) => {
                                        if (onClick) {
                                            e.preventDefault();
                                            onClick(e);
                                        }
                                    }}
                                >
                                    {ctaLabel || ""}
                                    <img
                                        src={buttonIcon}
                                        alt="Button Icon"
                                        className="ml-2 h-[12px] w-[12px]"
                                    />
                                </a>
                            )}
                        </>
                    );
                case "Facebook - Footer":
                    return (
                        <a href={href} target={target === "Same" ? "_self" : "_blank"} className={`block h-6 w-6 ${style}`}>
                            <img src={facebookIcon} alt="Facebook" className="h-6 w-6" />
                        </a>
                    );
                case "Instagram - Footer":
                    return (
                        <a href={href} target={target === "Same" ? "_self" : "_blank"} className={`block h-6 w-6 ${style}`}>
                            <img src={instagramIcon} alt="Instagram" className="h-6 w-6" />
                        </a>
                    );
                case "Resource(Download)":
                    return (

                        <a href={href} target={target === "Same" ? "_self" : "_blank"} onClick={onClick} className={`text-dark_green flex items-center gap-2 font-[700] hover:no-underline ${style}`}>
                            {ctaLabel ? ctaLabel : null}
                            <img src={downloadIcon} alt="Download Icon" />
                        </a>

                    );
                case "Resource(Watch-Video)":
                    return (

                        <button to={href} target={target === "Same" ? "_self" : "_blank"} onClick={onClick} className={`text-dark_green watch-video flex items-center gap-2 font-[700] hover:no-underline ${style}`}>
                            {ctaLabel ? ctaLabel : null}
                            <img src={watchVideoIcon} alt="Watch Video Icon" />
                        </button>
                    );
                case "Resource(Learn-More)":
                    return (
                        <a href={href} target={target === "Same" ? "_self" : "_blank"} className={`text-dark_green flex items-center gap-2 font-[700] hover:no-underline ${style}`}>
                            {ctaLabel ? ctaLabel : null}
                            <img src={learnMoreIcon}></img>
                        </a>
                    );
                default:
                    return handleError(`Unknown CTA type: ${type}`);
            }
        } catch (error) {
            return handleError(`Error rendering CTA: ${error.message}`);
        }
    };

    // If label is missing for non-social media CTAs, show error
    if (!label && !displayType?.includes("Footer")) {
        return handleError("CTA requires a label");
    }

    // Render the specified displayType
    return displayType ? renderCta(displayType) : handleError("CTA requires a displayType");
}